"use client";

import { usePathname } from "next/navigation";
import { blogLocaleFromPathname } from "@/lib/blog/locales";
import { parseLocaleFromPathname, resolveMarketingRoute } from "@/lib/i18n/paths";
import NextLink from "next/link";
import { beginNavigationProgressHandoff, clearNavigationProgressHandoff } from "@/lib/navigation-progress-handoff";
import { useCallback, useEffect, useState, type ComponentProps } from "react";
import { CcNavigationProgress } from "./CcNavigationProgress";
import { subscribePrimaryNavPrefetch } from "@/lib/primary-nav-prefetch";

type Props = ComponentProps<typeof NextLink> & {
  /** Default intent-only. `"primary"` registers a post-paint full prefetch. */
  priority?: "intent" | "primary";
};

function resolveHref(href: ComponentProps<typeof NextLink>["href"]): string | null {
  if (typeof href === "string") return href;
  if (href && typeof href === "object" && typeof href.pathname === "string") {
    return `${href.pathname}${href.search ?? ""}`;
  }
  return null;
}

function PrimaryPrefetch({
  href,
  enabled,
  onReady,
}: {
  href: string;
  enabled: boolean;
  onReady: () => void;
}) {
  useEffect(() => {
    if (!enabled) return;
    return subscribePrimaryNavPrefetch(href, onReady);
  }, [href, enabled, onReady]);
  return null;
}

export default function CcLink({
  onMouseEnter,
  onFocus,
  onTouchStart,
  onNavigate,
  prefetch,
  priority = "intent",
  href,
  children,
  ...props
}: Props) {
  // Reading pages reserve background bandwidth for their relevant next destinations.
  const pathname = usePathname();
  const { locale, pathWithoutLocale } = parseLocaleFromPathname(pathname ?? "/");
  const allowPrimary = blogLocaleFromPathname(pathname ?? "/") === null
    && resolveMarketingRoute(pathWithoutLocale, locale ?? "en")?.kind !== "project";
  const [intent, setIntent] = useState(false);
  const [readyHref, setReadyHref] = useState<string | null>(null);
  const resolvedHref = resolveHref(href);
  const markPrimaryReady = useCallback(() => {
    if (resolvedHref) setReadyHref(resolvedHref);
  }, [resolvedHref]);
  const primaryReady = priority === "primary" && allowPrimary && resolvedHref != null && readyHref === resolvedHref;

  const warm = () => {
    const connection = (navigator as Navigator & {connection?: {saveData?: boolean; effectiveType?: string}}).connection;
    if (prefetch === false || connection?.saveData || /^(slow-)?2g$/.test(connection?.effectiveType ?? "")) return;
    setIntent(true);
  };

  return <NextLink {...props} href={href} prefetch={prefetch === undefined ? intent || primaryReady : prefetch}
    onNavigate={event => {
      let prevented = false;
      onNavigate?.({ ...event, preventDefault: () => { prevented = true; event.preventDefault(); } });
      if (!prevented) {
        clearNavigationProgressHandoff();
        if (resolvedHref) {
          const destination = new URL(resolvedHref, window.location.href);
          if (destination.origin === window.location.origin && `${destination.pathname}${destination.search}` !== `${window.location.pathname}${window.location.search}`)
            beginNavigationProgressHandoff();
        }
      }
    }}
    onMouseEnter={event => { onMouseEnter?.(event); if (!event.defaultPrevented) warm(); }}
    onTouchStart={event => { onTouchStart?.(event); if (!event.defaultPrevented) warm(); }}
    onFocus={event => { onFocus?.(event); if (!event.defaultPrevented) warm(); }}>
    {priority === "primary" && resolvedHref
      ? <PrimaryPrefetch href={resolvedHref} enabled={prefetch !== false && allowPrimary} onReady={markPrimaryReady} />
      : null}
    {children}<CcNavigationProgress href={resolvedHref} />
  </NextLink>;
}
